// Users Store
import { camelCase, clone, find, isArray, isEmpty, isObject, upperFirst } from 'lodash'
import { getField, updateField } from 'vuex-map-fields'

const address = {
  firstname: null,
  city: null,
  address: null,
  house_number: [],
  lastname: null,
  country: {},
  gender: {},
  zip: null,
}

const user = {
  admin_url: '',
  areas: [],
  birthday: null,
  careers: [],
  email: null,
  firstname: null,
  gender: {},
  id: null,
  img_path: null,
  languages: [],
  lastname: null,
  locations: [],
  newsletter_subscriptions: [],
  skills: [],
  special_tasks: [],
  title: null,
}

const state = {
  user: clone(user),
  users: [],
  pagination: {},
  pending: false,
  filterActive: false,
  filterOptions: [],
  filters: {},
  userEditLanguages: [],
  userTopics: [],
  userChildren: [],
  invoiceAddress: {},
  deliveryAddress: {},
  genderOptions: [],
  countryOptions: [],
  statusOptions: [],
  salutationOptions: [],
  userCustomFields: [],
  registrationFields: [],
  userFieldIdentifiers: [],
  followedUsers: [],
  searchSuggestions: [],
  searchedUsers: {},
  orderBy: undefined,
}

const getters = {
  getField,
}

const actions = {
  getUsers({ commit }, payload) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .getItems('users', payload)
      .then(({ data }) => {
        commit('SET_USERS', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  getSearchedUsers({ commit }, payload = '') {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.users
        .searchUser(payload)
        .then(({ data }) => {
          commit('SET_SEARCH_USERS', data)
          commit('SET_PENDING', { pending: false })
          resolve()
        })
    })
  },
  getUser({ commit }, id) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .getItem(`users.${id}`)
      .then(({ data }) => {
        commit('SET_USER', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  getUserEditLanguages({ commit, state }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.users
      .getUserEditLanguages(state.user.id)
      .then(({ data }) => {
        commit('SET_USER_EDIT_LANGUAGES', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  getUserFilters({ commit }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .getFilters('users')
      .then(({ data }) => {
        commit('SET_USER_FILTERS', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  updateAvatar({ commit, state }, img) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.users
      .putAvatar(state.user.id, img.id, img.coordinates)
      .then(({ data }) => {
        commit('SET_PENDING', { pending: false })
        resolve(data)
      })
    })
  },
  editProfile({ commit, state }, data) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .editItem(`users.${state.user.id}`, data)
      .then(({ data }) => {
        commit('SET_PENDING', { pending: false })
        resolve(data)
      })
    })
  },
  searchUsers({ commit }, payload) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .searchItems('users', payload)
      .then(({ data }) => {
        commit('SET_USERS', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  filterUsers({ commit, state }, { page, query, order_by }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
        .filterItems('users', {
          query,
          order_by,
          ...state.filters,
          page,
        })
        .then(({ data }) => {
          commit('SET_USERS', data)
          commit('SET_FILTER_ACTIVE')
          commit('SET_PENDING', { pending: false })
          resolve()
        })
    })
  },
  getPersonalDataTopics({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingTopics' })
      this.api.items
        .getItems('topics', { id })
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_PERSONAL_DATA_TOPICS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_PERSONAL_DATA_TOPICS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_PERSONAL_DATA_TOPICS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingTopics' })
        })
    })
  },
  getInvoiceAddress({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingInvoiceAddress' })
      this.api.items
        .getItems(`users.${id}.invoice_address`)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_INVOICE_ADDRESS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_INVOICE_ADDRESS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_INVOICE_ADDRESS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingInvoiceAddress' })
        })
    })
  },
  getDeliveryAddress({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingDeliveryAddress' })
      this.api.items
        .getItems(`users.${id}.delivery_address`)
        .then(({ data }) => {
          if (data && data.errors) {
            commit('SET_DELIVERY_ADDRESS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_DELIVERY_ADDRESS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_DELIVERY_ADDRESS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingDeliveryAddress' })
        })
    })
  },
  getGenderOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingGenderOption' })
      this.api.items
        .getItems('users.gender_options')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_GENDER_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_GENDER_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_GENDER_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingGenderOption' })
        })
    })
  },
  getStatusOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingStatusOption' })
      this.api.items
        .getItems('users.status_options')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_STATUS_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_STATUS_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_STATUS_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingStatusOption' })
        })
    })
  },
  getCountryOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingCountryOption' })
      this.api.items
        .getItems('addresses.country_options')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_COUNTRY_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_COUNTRY_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_COUNTRY_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingCountryOption' })
        })
    })
  },
  getSalutationOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingSalutationOption' })
      this.api.items
        .getItems('addresses.salutation_options')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_SALUTATION_OPTIONS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_SALUTATION_OPTIONS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_SALUTATION_OPTIONS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingSalutationOption' })
        })
    })
  },
  getChildrensData({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingChildrens' })
      this.api.items
        .getItems(`users.${id}.children`)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_CHILDRENS_DATA_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_CHILDRENS_DATA_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_CHILDRENS_DATA_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingChildrens' })
        })
    })
  },
  getCustomFields({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingCustomeFields' })
      this.api.items
        .getItems(`users.custom_profile_fields.${id}`)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_CUSTOM_FIELDS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_CUSTOM_FIELDS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_CUSTOM_FIELDS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingCustomeFields' })
        })
    })
  },
  updateAddress({ commit }, { addressType, address, id }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: `save${upperFirst(camelCase(addressType))}` })
      this.api.items
        .createItem(`users.${id}.${addressType}`, address)
        .then(({ data }) => {
          if (data.errors) {
            commit('UPDATE_ADDRESS_FAIL')
            reject(data.errors)
            return
          }

          commit('UPADTE_ADDRESS_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('UPDATE_ADDRESS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: `save${upperFirst(camelCase(addressType))}` })
        })
    })
  },
  updateTopics({ commit, state }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'saveTopics' })
      this.api.items
        .createItem(`users.${id}.user_data`, state.userTopics)
        .then(({ data }) => {
          if (data.errors) {
            commit('UPDATE_TOPICS_FAIL')
            reject(data.errors)
            return
          }

          commit('UPDATE_TOPICS_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('UPDATE_TOPICS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'saveTopics' })
        })
    })
  },
  saveChildrenData({ commit }, { id, children }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'saveChildren' })
      this.api.items
        .createItem(`users.${id}.children`, { children: children })
        .then(({ data }) => {
          if (data.errors) {
            commit('SAVE_CHILDRENS_DATA_FAIL')
            reject(data.errors)
            return
          }
          commit('SAVE_CHILDRENS_DATA_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('SAVE_CHILDRENS_DATA_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'saveChildren' })
        })
    })
  },
  saveCustomFields({ commit }, { id, customFields }) {
    const fieldSet = customFields.custom_profile_fields
    if (fieldSet) {
      for (const field in fieldSet) {
        if (isArray(fieldSet[field])) {
          const tmpField = fieldSet[field][0]
          fieldSet[field] = isObject(tmpField) && tmpField.key && tmpField.key === 'text_value' ? tmpField.text : fieldSet[field]
        }
      }
      customFields.custom_profile_fields = fieldSet
    }
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'saveCustomFields' })
      this.api.items
        .createItem(`users.custom_profile_fields.${id}`, customFields)
        .then(({ data }) => {
          if (data.errors) {
            commit('SAVE_CUSTOM_FIELDS_FAIL')
            reject(data.errors)
            return
          }
          commit('SAVE_CUSTOM_FIELDS_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('SAVE_CUSTOM_FIELDS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'saveCustomFields' })
        })
    })
  },
  deleteUser({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'deleteAccount' })
      this.api.items
      .deleteItem(`users.${id}`)
      .then(({ data })=> {
        if (data.errors) {
          commit('REMOVE_ACCOUNT_FAIL')
          reject(data.errors)
          return
        }
        window.location.href = '/'
        commit('REMOVE_ACCOUNT_SUCCESS')
        resolve(data)
      })
      .catch(errors => {
        commit('REMOVE_ACCOUNT_FAIL')
        reject(errors)
      })
      .finally(_ => {
        commit('SET_PENDING', { pending: false, type: 'deleteAccount' })
      })
    })
  },
  getRegistrationFields({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingRegistrationFields' })
      this.api.items
        .getItems(`users.${id}.registration_fields`)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_REGISTRATION_FIELDS_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_REGISTRATION_FIELDS_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_REGISTRATION_FIELDS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingRegistrationFields' })
        })
    })
  },
  saveRegistrationFields({ commit }, { id, registrationFields }) {
    const fieldSet = registrationFields.custom_profile_fields
    if (fieldSet) {
      for (const field in fieldSet) {
        if (isArray(fieldSet[field])) {
          const tmpField = fieldSet[field][0]
          fieldSet[field] = isObject(tmpField) && tmpField.key && tmpField.key === 'text_value' ? tmpField.text : fieldSet[field]
        }
      }
      registrationFields.custom_profile_fields = fieldSet
    }
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'saveCustomFields' })
      this.api.items
        .createItem(`users.${id}.registration_fields`, registrationFields)
        .then(({ data }) => {
          if (data.errors) {
            commit('SAVE_REGISTRATION_FIELDS_FAIL')
            reject(data.errors)
            return
          }
          commit('SAVE_REGISTRATION_FIELDS_SUCCESS')
          resolve(data)
        })
        .catch(errors => {
          commit('SAVE_REGISTRATION_FIELDS_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'saveCustomFields' })
        })
    })
  },
  getUsersListOptions({ commit }) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingListOptions' })
      this.api.items
        .getItems('users.list_options')
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_USER_IDENTIFIER_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_USER_IDENTIFIER_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_USER_IDENTIFIER_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingListOptions' })
        })
    })
  },
  getFollowedUsersList({ commit }, id) {
    return new Promise((resolve, reject) => {
      commit('SET_PENDING', { pending: true, type: 'pendingFollowedUsersList' })
      this.api.items
        .getItems(`users.${id}.follows`)
        .then(({ data }) => {
          if (data.errors) {
            commit('SET_FOLLOWED_USERS_LIST_FAIL')
            reject(data.errors)
            return
          }

          commit('SET_FOLLOWED_USERS_LIST_SUCCESS', data)
          resolve(data)
        })
        .catch(errors => {
          commit('SET_FOLLOWED_USERS_LIST_FAIL')
          reject(errors)
        })
        .finally(_ => {
          commit('SET_PENDING', { pending: false, type: 'pendingFollowedUsersList' })
        })
    })
  },
  followUser({ commit }, { activeUserId, selectedUserId }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .editItem(`users.${activeUserId}.follows`, { follows: [selectedUserId] })
      .then(({ data }) => {
        commit('SET_PENDING', { pending: false })
        resolve(data)
      })
    })
  },
  unfollowUser({ commit }, { activeUserId, selectedUserId }) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .deleteItem(`users.${activeUserId}.follows`, { follows: [selectedUserId] })
      .then(({ data }) => {
        commit('SET_PENDING', { pending: false })
        resolve(data)
      })
    })
  },
  getSearchSuggestions({ commit }, payload) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .searchItems('users.profile_fields', payload)
      .then(({ data }) => {
        commit('SET_SEARCH_SUGGESTIONS', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  getUsersBasedOnSuggestions({ commit }, payload) {
    return new Promise(resolve => {
      commit('SET_PENDING', { pending: true })
      this.api.items
      .searchItems('users', payload)
      .then(({ data }) => {
        commit('SET_USERS_BASED_ON_SUGGESTIONS', data)
        commit('SET_PENDING', { pending: false })
        resolve()
      })
    })
  },
  setFilterActive({ commit }) {
    commit('SET_FILTER_ACTIVE')
  },
  updateFilters({ commit }, data) {
    commit('UPDATE_FILTERS', data)
  },
  setFiltersFromQuery({ commit }, data) {
    commit('UPDATE_FILTERS_FROM_QUERY', data)
  },
  resetFilter({ commit }) {
    commit('RESET_FILTER')
  },
  resetUserEditLanguages({ commit }) {
    commit('RESET_USER_EDIT_LANGUAGES')
  },
  clearUser({ commit }) {
    commit('CLEAR_USER')
  },
  clearUsers({ commit }) {
    commit('CLEAR_USERS')
  },
  updateDataTopic({ commit }, args) {
    commit('UPDATE_DATA_TOPIC', args)
  },
  updateRadioDataTopic({ commit }, args) {
    commit('UPDATE_RADIO_DATA_TOPIC', args)
  },
  updateSubscriptionsOptions({ commit }, args) {
    commit('UPDATE_NEWS_SUBSCRIPTION_OPTION', args)
  },
  resetUserIdentifiers({ commit }) {
    commit('RESET_USER_IDENTIFIER')
  },
  updateCareers({ commit, state }, careers) {
    commit('UPDATE_USER_CAREER', careers)
  },
}

const mutations = {
  SET_PENDING: (state, { pending, type = 'pending' }) => {
    state[type] = pending
  },
  CLEAR_USER(state) {
    state.user = clone(user)
    state.errors = {}
  },
  CLEAR_USERS(state) {
    state.filterActive = false
    state.filterOptions = []
    state.pagination = {}
    state.users = []
    state.orderBy = undefined
  },
  RESET_FILTER: state => {
    state.filterActive = false
    state.filters = {}
  },
  SET_USER_FILTERS: (state, data) => {
    state.filterOptions = data
  },
  UPDATE_FILTERS: (state, data) => {
    state.filters = data
  },
  UPDATE_FILTERS_FROM_QUERY: (state, data) => {
    state.filters = data
  },
  SET_FILTER_ACTIVE: state => {
    state.filterActive = true
  },
  RESET_USER_EDIT_LANGUAGES: state => {
    state.userEditLanguages = {}
  },
  SET_USERS: (state, { results, pagination, order_by }) => {
    state.orderBy = order_by
    state.users = results
    state.pagination = pagination
  },
  SET_USER: (state, user) => {
    state.user = user
  },
  SET_USER_EDIT_LANGUAGES: (state, data) => {
    state.userEditLanguages = data
  },
  SET_PERSONAL_DATA_TOPICS_SUCCESS(state, topics) {
    state.userTopics = topics
  },
  SET_CHILDRENS_DATA_SUCCESS(state, childrens) {
    state.userChildren = childrens
  },
  SET_INVOICE_ADDRESS_SUCCESS(state, invoiceAddress) {
    if (isEmpty(invoiceAddress)) {
      state.invoiceAddress = address
    } else {
      state.invoiceAddress = invoiceAddress
    }
  },
  SET_DELIVERY_ADDRESS_SUCCESS(state, deliveryAddress) {
    if (isEmpty(deliveryAddress)) {
      state.deliveryAddress = address
    } else {
      state.deliveryAddress = deliveryAddress
    }
  },
  SET_SEARCH_SUGGESTIONS(state, data) {
    state.searchSuggestions = data.results
  },
  SET_USERS_BASED_ON_SUGGESTIONS(state, data) {
    state.searchedUsers = data
  },
  SET_GENDER_OPTIONS_SUCCESS(state, genderOptions) {
    state.genderOptions = genderOptions
  },
  SET_STATUS_OPTIONS_SUCCESS(state, statusOptions) {
    state.statusOptions = statusOptions
  },
  SET_COUNTRY_OPTIONS_SUCCESS(state, countryOptions) {
    state.countryOptions = countryOptions
  },
  SET_SALUTATION_OPTIONS_SUCCESS(state, salutationOptions) {
    state.salutationOptions = salutationOptions
  },
  SET_CUSTOM_FIELDS_SUCCESS(state, customFields) {
    state.userCustomFields = customFields
  },
  SET_REGISTRATION_FIELDS_SUCCESS(state, registrationFields) {
    state.registrationFields = registrationFields
  },
  UPDATE_DATA_TOPIC: (state, { topic_id, option_id, args }) => {
    const topic = find(state.userTopics, { id: topic_id })
    const option = find(topic.options, { id: option_id })
    option.user_selected = args
  },
  UPDATE_RADIO_DATA_TOPIC: (state, { topic_id, option_id }) => {
    const topic = find(state.userTopics, { id: topic_id })
    topic.options.forEach(opt => {
      opt.user_selected = opt.id === option_id
    })
  },
  UPDATE_NEWS_SUBSCRIPTION_OPTION: (state, { key, args }) => {
    const option = find(state.user.newsletter_subscriptions, { key: key })
    option.selected = args
  },
  SET_USER_IDENTIFIER_SUCCESS(state, data) {
    if (data && data.fields) {
      data.fields.forEach(field => {
        state.userFieldIdentifiers.push(field.identifier)
      })
    }
  },
  RESET_USER_IDENTIFIER(state) {
    state.userFieldIdentifiers = []
  },
  SET_SEARCH_USERS: (state, payload) => {
    state.users = payload
  },
  SET_FOLLOWED_USERS_LIST_SUCCESS: (state, payload) => {
    state.followedUsers = payload.users
  },
  UPDATE_USER_CAREER(state, payload) {
    state.user = { ...state.user, careers: payload }
  },
  updateField,
}

/*
 * This commits are only for the vuex history (visible in the vue devtools tab 2)
 * if you need to store data with any history commit, then remove the commit from
 * the history_commits and create a new commit in the mutation block.
 */
const history_commits = [
  'SET_PERSONAL_DATA_TOPICS_FAIL',
  'SET_CHILDRENS_DATA_FAIL',
  'SET_INVOICE_ADDRESS_FAIL',
  'SET_DELIVERY_ADDRESS_FAIL',
  'SET_GENDER_OPTIONS_FAIL',
  'SET_STATUS_OPTIONS_FAIL',
  'SET_COUNTRY_OPTIONS_FAIL',
  'SET_SALUTATION_OPTIONS_FAIL',
  'UPDATE_ADDRESS_FAIL',
  'UPADTE_ADDRESS_SUCCESS',
  'UPDATE_TOPICS_FAIL',
  'UPDATE_TOPICS_SUCCESS',
  'SAVE_CHILDRENS_DATA_SUCCESS',
  'SAVE_CHILDRENS_DATA_FAIL',
  'SET_CUSTOM_FIELDS_FAIL',
  'SAVE_CUSTOM_FIELDS_SUCCESS',
  'SAVE_CUSTOM_FIELDS_FAIL',
  'REMOVE_ACCOUNT_FAIL',
  'REMOVE_ACCOUNT_SUCCESS',
  'SET_REGISTRATION_FIELDS_FAIL',
  'SAVE_REGISTRATION_FIELDS_FAIL',
  'SAVE_REGISTRATION_FIELDS_SUCCESS',
  'SET_USER_IDENTIFIER_FAIL',
  'SET_FOLLOWED_USERS_LIST_FAIL',
]

history_commits.forEach(commit => {
  mutations[commit] = _ => true
})

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
